import { css } from '@emotion/core'
import { useHistory } from '@fs/zion-router'
import { Grid, Select, H5 } from '@fs/zion-ui'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { convertUTCString, getLocalTimeZoneAbbreviation } from '../../../util/DateUtil'
import DmcCopyToClipboardTooltip from '../../../util/DmcCopyToClipboardTooltip'
import { printValueOrBooleanReadable } from '../../../util/StringUtil'
import DataColumnView from '../../common/DataColumnView'
import { UserContext } from '../UserContext'
import UserOrdinances from './UserOrdinances'
import UserRelationships from './UserRelationships'
import { CmisLink, MrnLink } from '../../app/Links'
import { getFrontendBaseUrl } from '../../../util/UrlUtil'

const CMIS_REF_ID = 'cmisRefId'
const MRN_ID = 'mrn'
const UUID = 'uuid'
const PREVIOUS_MRN_LIST = 'previousMrnList'
const PREVIOUS_CMIS_REF_ID_LIST = 'previousCmisReferenceId'
const UNIT_FIELD_NAME = 'unit'
const LAST_UPDATED_BY_MEMBERSHIP = 'lastUpdatedByMembership'
const HOME_ADDRESS = 'homeAddress'
const MAIL_ADDRESS = 'mailAddress'
/* Depending on how wide the screen is, different flex-direction: column heights
 * are needed in <DataColumnView />
 * This array also varies depending on how many tuples are displayed. */
const previousMrnCmisRefDropdown = css`
  display: inline-block;
  width: 50%;
  height: 0px;

  select {
    max-height: 20px;
  }
`

export default function UserMembership() {
  const history = useHistory()
  const userContext = useContext(UserContext)
  const currentUser = userContext.currentUser
  const dmcUserMembership = currentUser.dmcUserMembership
  const [cmsData, setCmsData] = useState()
  const [t] = useTranslation()

  useEffect(() => {
    const createFieldConfig = (name, label) => ({ name, label })

    const CMS_FIELDS = [
      createFieldConfig(MRN_ID, t('mrn', 'MRN')),
      createFieldConfig(CMIS_REF_ID, t('membership.cmisRefId', 'CMIS Ref Id')),
      createFieldConfig(UUID, t('membership.uuid', 'UUID')),
      createFieldConfig(PREVIOUS_MRN_LIST, t('membership.previousMrn', 'Previous MRN')),
      createFieldConfig(PREVIOUS_CMIS_REF_ID_LIST, t('membership.previousCmisRefId', 'Previous CMIS Ref Id')),
      createFieldConfig('member', t('membership.churchMember', 'Church Member')),
      createFieldConfig('officialName', t('membership.officialName', 'Official Name')),
      createFieldConfig('maidenName', t('membership.maidenName', 'Maiden Name')),
      createFieldConfig('gender', t('gender.label', 'Gender')),
      createFieldConfig('livingStatus', t('membership.livingStatus', 'Living Status')),
      createFieldConfig(UNIT_FIELD_NAME, t('nav.unit', 'Unit')),
      createFieldConfig('birthDate', t('birthDate', 'Birth Date')),
      createFieldConfig('birthPlace', t('membership.birthPlace', 'Birth Place')),
      createFieldConfig(HOME_ADDRESS, t('membership.homeAddress', 'Home Address')),
      createFieldConfig(MAIL_ADDRESS, t('membership.mailAddress', 'Mail Address')),
      dmcUserMembership?.localPhoneNumber
        ? createFieldConfig('localPhoneNumber', t('membership.localPhoneNumber', 'Local Phone Number'))
        : createFieldConfig('phone', t('membership.phone', 'Phone')),
      dmcUserMembership?.internationalPhoneNumber &&
        createFieldConfig(
          'internationalPhoneNumber',
          t('membership.internationalPhoneNumber', 'International Phone Number')
        ),
      createFieldConfig('deathDate', t('deathDate', 'Death Date')),
      createFieldConfig(
        LAST_UPDATED_BY_MEMBERSHIP,
        t('membership.lastUpdatedByMembership', 'Last Updated by Membership')
      ),
      createFieldConfig(
        'tfMembershipGenderMatch',
        t('membership.tfMembershipGenderMatch', 'Tree and Membership Sex Match')
      ),
      createFieldConfig('membershipStatus', t('membership.membershipStatus', 'Membership Status')),
      createFieldConfig('membershipStatusReason', t('membership.membershipStatusReason', 'Membership Status Reason')),
    ]

    const createListDetail = (values, contextType) => {
      if (values?.length > 1) {
        return (
          <span css={previousMrnCmisRefDropdown}>
            <Select
              autoWidth
              placeholder=""
              options={values.map((value, index) => ({ value, label: `${index + 1} - ${value}` }))}
              onChange={(e) =>
                window.open(
                  `${getFrontendBaseUrl()}/user/${e.target.value}/summary?context=${contextType}&forward=false`,
                  '_blank'
                )
              }
            />
          </span>
        )
      }
      const LinkComponent = contextType === 'mrn' ? MrnLink : CmisLink
      const props = contextType === 'mrn' ? { mrn: values } : { cmisId: values }
      return values ? (
        <DmcCopyToClipboardTooltip value={values} body={<LinkComponent {...props} forward={false} />} nextToText />
      ) : null
    }

    const createCmsItem = (field) => {
      const fieldValue = dmcUserMembership?.[field.name]

      switch (field.name) {
        case UNIT_FIELD_NAME:
          return {
            label: field.label,
            detail: fieldValue ? (
              <DmcCopyToClipboardTooltip path={`/unit/${fieldValue}`} value={fieldValue} nextToText newTab />
            ) : null,
          }
        case MRN_ID:
        case CMIS_REF_ID:
        case UUID:
          return {
            label: field.label,
            detail: fieldValue ? <DmcCopyToClipboardTooltip value={fieldValue} nextToText /> : null,
          }
        case PREVIOUS_CMIS_REF_ID_LIST:
          return { label: field.label, detail: createListDetail(fieldValue, 'cmisId') }
        case PREVIOUS_MRN_LIST:
          return { label: field.label, detail: createListDetail(fieldValue, 'mrn') }
        case LAST_UPDATED_BY_MEMBERSHIP:
          return {
            label: field.label,
            detail: fieldValue ? `${convertUTCString(fieldValue, true)} ${getLocalTimeZoneAbbreviation()}` : null,
          }
        case HOME_ADDRESS:
        case MAIL_ADDRESS:
          return {
            label: field.label,
            detail: fieldValue ? (
              <DmcCopyToClipboardTooltip
                value={fieldValue}
                body={fieldValue}
                overlayMessage={t('membership.addressCopiedMessage', 'Address copied to clipboard')}
                nextToText
              />
            ) : null,
          }
        case 'gender':
        case 'livingStatus':
        case 'membershipStatus':
          return {
            label: field.label,
            detail: fieldValue ? t(`${field.name}.${fieldValue}`, fieldValue) : null,
          }
        default:
          return {
            label: field.label,
            detail: printValueOrBooleanReadable(fieldValue) ?? fieldValue,
          }
      }
    }

    const cmsList = CMS_FIELDS.filter(Boolean).map(createCmsItem)
    setCmsData(cmsList)
  }, [dmcUserMembership, history, t])

  if (!dmcUserMembership) return null

  return (
    <Grid>
      <H5>{t('user.membership', 'User Membership')}</H5>
      <DataColumnView data={cmsData} />
      <H5>{t('ordinances.label', 'Ordinances')}</H5>
      <UserOrdinances />
      <UserRelationships />
    </Grid>
  )
}
